import styled, { css, keyframes } from "styled-components";

import { colorsPalettes, setElevation } from "@similarweb/styles";

import {
    ONBOARDING_AGENT_PANEL_RESIZE_TRANSITION_MS,
    ONBOARDING_AGENT_TRIGGER_SIZE,
    PANEL_Z_INDEX,
} from "../OnboardingAgent/constants";
import { ONBOARDING_AGENT_AI_GRADIENT_COLORS } from "../onboardingAgentAiGradientTokens";

import { ONBOARDING_AGENT_TRIGGER_BOUNCE_DURATION_MS, TRIGGER_ICON_SIZE_PX } from "./constants";

type FixedSlotProps = {
    readonly $x: number;
    readonly $y: number;
    readonly $shouldAnimatePosition?: boolean;
};

export const FixedSlot = styled.div<FixedSlotProps>`
    position: fixed;
    left: ${({ $x }) => $x}px;
    top: ${({ $y }) => $y}px;
    z-index: ${PANEL_Z_INDEX};
    ${({ $shouldAnimatePosition }) =>
        $shouldAnimatePosition
            ? `
                transition:
                    left ${ONBOARDING_AGENT_PANEL_RESIZE_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
                    top ${ONBOARDING_AGENT_PANEL_RESIZE_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
            `
            : ""}
`;

/** Squash-and-stretch bounce for the whole trigger (mirrors the "Bounce" reference: a hop off the bottom edge). */
const bounceKf = keyframes`
    0% {
        transform: translateY(0) scale(1, 1);
    }
    30% {
        transform: translateY(-10px) scale(0.94, 1.06);
    }
    50% {
        transform: translateY(0) scale(1.08, 0.92);
    }
    62% {
        transform: translateY(-3px) scale(0.98, 1.02);
    }
    75%,
    100% {
        transform: translateY(0) scale(1, 1);
    }
`;

/** Sparkle "pop" that plays in sync with the bounce, matching the reference's `bpop` keyframe. */
const sparklePopKf = keyframes`
    0%,
    46% {
        transform: scale(0.7) rotate(0deg);
    }
    55% {
        transform: scale(1.3) rotate(20deg);
    }
    80%,
    100% {
        transform: scale(1) rotate(0deg);
    }
`;

type TriggerButtonProps = {
    readonly $isBouncing?: boolean;
};

export const TriggerButton = styled.button<TriggerButtonProps>`
    box-sizing: border-box;
    width: ${ONBOARDING_AGENT_TRIGGER_SIZE}px;
    height: ${ONBOARDING_AGENT_TRIGGER_SIZE}px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 11px 8px 5px 8px;
    margin: 0;
    border: none;
    border-radius: 16px;
    background-image: linear-gradient(
        90deg,
        ${ONBOARDING_AGENT_AI_GRADIENT_COLORS.triggerStart} 0%,
        ${ONBOARDING_AGENT_AI_GRADIENT_COLORS.triggerEnd} 100%
    );
    cursor: pointer;
    ${setElevation("Z3")}
    ${({ $isBouncing }) =>
        $isBouncing
            ? css`
                  transform-origin: center bottom;
                  animation: ${bounceKf} ${ONBOARDING_AGENT_TRIGGER_BOUNCE_DURATION_MS}ms cubic-bezier(0.5, 0, 0.5, 1);
              `
            : ""}

    &:focus-visible {
        outline: 2px solid ${colorsPalettes.blue[400]};
        outline-offset: 2px;
    }
`;

export const IconFrame = styled.span`
    display: block;
    width: ${TRIGGER_ICON_SIZE_PX}px;
    height: ${TRIGGER_ICON_SIZE_PX}px;
    flex-shrink: 0;
`;

type AnimatedSparklePathProps = {
    readonly $isBouncing?: boolean;
};

export const AnimatedSparklePath = styled.path<AnimatedSparklePathProps>`
    transform-box: fill-box;
    transform-origin: center;
    ${({ $isBouncing }) =>
        $isBouncing
            ? css`
                  animation: ${sparklePopKf} ${ONBOARDING_AGENT_TRIGGER_BOUNCE_DURATION_MS}ms ease-out;
              `
            : ""}
`;
